import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/site/ProductReviews";
import { useLang } from "@/lib/i18n";

export const reviewDate = (d, lang = "fr") => new Date(d).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
export const fmtNote = (n) => Number(n || 0).toFixed(1).replace(".", ",");
export const fmtCount = (n) => Number(n || 0).toLocaleString("fr-FR");

/** En anglais, les avis sont servis traduits (champ `translated`), toujours signalés comme tels. */
export function useShopReviews(params) {
  const { lang } = useLang();
  const p = { ...params, ...(lang === "en" ? { lang: "en" } : {}) };
  return useQuery({
    queryKey: ["shop-reviews", p],
    queryFn: () => api.get("/shop-reviews", { params: p }).then((r) => r.data),
    staleTime: 300000,
    keepPreviousData: true,
  });
}

/** Petit badge « 4,9/5 · 1 170 avis certifiés » qui mène à la page des avis. */
export function ShopRating({ testid = "shop-rating", className = "" }) {
  const { data } = useShopReviews({ per_page: 1 });
  const { t } = useLang();
  if (!data?.count) return null;
  return (
    <Link to="/avis-clients" data-testid={testid} className={`inline-flex flex-wrap items-center gap-2 text-sm text-[var(--parchment)]/90 transition-colors hover:text-[var(--gold-soft)] ${className}`}>
      <Stars n={Math.round(data.note)} /> <b className="text-[var(--parchment)]">{fmtNote(data.note)}/5</b>
      <span className="underline underline-offset-4">{fmtCount(data.count)} {t("avis clients certifiés")}</span>
    </Link>
  );
}

export function ReviewCard({ r, testid }) {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <li data-testid={testid} className="flex flex-col rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
      <div className="flex items-center justify-between gap-3">
        <Stars n={Math.round(r.rating)} />
        <span className="text-xs text-[var(--parchment)]/75">{reviewDate(r.date, lang)}</span>
      </div>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-[var(--parchment)]/90 md:text-base">{en ? `“${r.text}”` : `« ${r.text} »`}</p>
      <p className="mt-4 flex flex-wrap items-center gap-3 text-sm font-semibold">{r.name}
        <span className="flex items-center gap-1 text-xs font-normal text-[var(--gold-soft)]"><BadgeCheck className="h-4 w-4" /> {en ? "Verified review" : "Avis certifié"}</span>
        {r.translated ? <span data-testid="review-translated" className="text-xs font-normal text-[var(--parchment)]/70">Translated from French</span> : null}
      </p>
    </li>
  );
}

/** Bloc de l'accueil : note globale + 3 avis récents. */
export function ShopReviewsHighlight() {
  const { data } = useShopReviews({ featured: true, per_page: 3 });
  const { lang } = useLang();
  const en = lang === "en";
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return null;
  return (
    <section data-testid="home-shop-reviews" className="px-5 py-24 md:px-10">
      <div className="mx-auto max-w-6xl">
        <p className="eyebrow mb-4">{en ? "Verified customer reviews" : "Avis clients certifiés"}</p>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display text-4xl leading-tight md:text-5xl">
            <span className="gold-text">{en ? data.note.toFixed(1) : fmtNote(data.note)}/5</span> {en ? `from ${Number(data.count).toLocaleString("en-GB")} reviews` : `sur ${fmtCount(data.count)} avis`}
          </h2>
          <Link to="/avis-clients" data-testid="home-shop-reviews-all" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)] underline underline-offset-4">
            {en ? "Read all reviews" : "Lire tous les avis"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <ul className="mt-10 grid gap-6 md:grid-cols-3">
          {items.map((r, i) => <ReviewCard key={`${r.date}-${i}`} r={r} testid={`home-shop-review-${i}`} />)}
        </ul>
        <p className="mt-6 text-xs text-[var(--parchment)]/75">{en ? `Reviews left after ordering on ozentya.com, collected by ${data.source}. Original reviews in French, translated faithfully.` : `Avis déposés après commande sur ozentya.com et collectés par ${data.source}.`}</p>
      </div>
    </section>
  );
}
